/**
 * Login page component - shadcn/ui
 */

import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import { PasswordInput } from '../../components/ui/password-input';
import { LanguageSelector } from '../../components/ui/language-selector';
import { AUTH_STORAGE_KEYS, useAuth, AuthApiError, authApi, adaptCrossDomainUrl, redirectToCustomDomain, launchSSO, getReturnPathFromState, DEFAULT_RETURN_PATH } from '../../shared/auth';
import type { LoginResponse, MfaChallengeResponse } from '../../shared/auth';
import { getRefreshToken } from '../../shared/auth/secureTokenStore';
import { MfaChallengeStep } from './components/MfaChallengeStep';
import { DemoEntry } from './components/DemoEntry';
import { SSOButtonList } from '../../shared/auth/SSOButtonList';
import { type SSOProviderId } from '../../shared/auth/ssoProviders';
import { supportsAppleNativeSignIn, triggerAppleNativeSignIn } from '../../shared/auth/appleNativeSignIn';
import { setSSOItem } from '../../shared/auth/ssoStorage';
import { isTauriDesktop, getOAuthRedirectUri, getAppleOAuthRedirectUri, isCustomDomain, getCurrentDomain, getClientPlatform, isLocalDev, getNativePlatform } from '../../shared/utils';
import { isCapacitor } from '../../shared/api/config';
import { DOWNLOAD_URLS } from '../../shared/config/downloadUrls';
import { getModuleById } from '../../shared/config/modules.config';
import { getTransportErrorMessage } from "@/shared/utils/errors";
import { useGeoLanguageDefault } from '../../i18n/useGeoLanguageDefault';

/* eslint-disable i18next/no-literal-string -- brand logo SVGs */
function GooglePlayBadge({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 135 40" className={className} role="img" aria-label="Get it on Google Play">
      <rect width="135" height="40" rx="5" fill="#000" />
      <text x="47" y="12" fill="#fff" fontSize="6" fontFamily="system-ui, sans-serif" letterSpacing="0.5">GET IT ON</text>
      <text x="47" y="27" fill="#fff" fontSize="13" fontFamily="system-ui, sans-serif" fontWeight="500">Google Play</text>
      <g transform="translate(10, 5) scale(0.75)">
        <path d="M6.1 3.4L20.5 11.7c.6.3.6 1.2 0 1.6L6.1 21.6c-.6.4-1.4 0-1.4-.8V4.2c0-.8.8-1.2 1.4-.8z" fill="#4285F4"/>
        <path d="M4.7 4.2v16.6L14.3 12.5 4.7 4.2z" fill="#2EC85A"/>
        <path d="M4.7 20.8l9.6-8.3 3.1 2.7-12.7 5.6z" fill="#F14D42"/>
        <path d="M4.7 4.2l12.7 5.6-3.1 2.7L4.7 4.2z" fill="#FFD400"/>
      </g>
    </svg>
  );
}

function AppStoreBadge({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 135 40" className={className} role="img" aria-label="Download on the App Store">
      <rect width="135" height="40" rx="5" fill="#000" />
      <text x="42" y="12" fill="#fff" fontSize="6" fontFamily="system-ui, sans-serif" letterSpacing="0.5">Download on the</text>
      <text x="42" y="27" fill="#fff" fontSize="13" fontFamily="system-ui, sans-serif" fontWeight="500">App Store</text>
      <g transform="translate(12, 7) scale(1.1)">
        <path d="M14.1 11.5c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.9-3.5.9s-1.9-.8-3.1-.8c-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3 2.4 1.2-.1 1.7-.8 3.1-.8s1.9.8 3.1.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1-.1-2.8-1.1-2.8-4.1z" fill="#fff"/>
        <path d="M11.5 4.7c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" fill="#fff"/>
      </g>
    </svg>
  );
}
/* eslint-enable i18next/no-literal-string */

function DownloadAppPrompt() {
  const { t } = useTranslation('auth');

  // Don't show if already running in a native wrapper
  if (isTauriDesktop() || isCapacitor) return null;

  const platform = getClientPlatform();

  switch (platform) {
    case 'macos':
    case 'windows': {
      const href = platform === 'macos' ? DOWNLOAD_URLS.mac : DOWNLOAD_URLS.windows;
      const label = platform === 'macos' ? t('download.mac') : t('download.windows');
      return (
        <div className="mt-4 text-center" data-testid="download-app-prompt">
          <p className="text-sm text-muted-foreground mb-2">{t('download.title')}</p>
          <Button variant="outline" size="sm" asChild>
            <a href={href} data-testid="download-app-link">
              <Download className="mr-1.5 h-4 w-4" />
              {label}
            </a>
          </Button>
        </div>
      );
    }
    case 'android': {
      const href = DOWNLOAD_URLS.android;
      const isPlaceholder = href === '#';
      return (
        <div className="mt-4 text-center" data-testid="download-app-prompt">
          <p className="text-sm text-muted-foreground mb-2">{t('download.title')}</p>
          {isPlaceholder ? (
            <div className="opacity-50">
              <GooglePlayBadge className="h-10 inline-block" />
              <p className="text-xs text-muted-foreground mt-1">{t('download.comingSoon')}</p>
            </div>
          ) : (
            <a href={href} data-testid="download-app-link">
              <GooglePlayBadge className="h-10 inline-block" />
            </a>
          )}
        </div>
      );
    }
    case 'ios': {
      const href = DOWNLOAD_URLS.ios;
      const isPlaceholder = href === '#';
      return (
        <div className="mt-4 text-center" data-testid="download-app-prompt">
          <p className="text-sm text-muted-foreground mb-2">{t('download.title')}</p>
          {isPlaceholder ? (
            <div className="opacity-50">
              <AppStoreBadge className="h-10 inline-block" />
              <p className="text-xs text-muted-foreground mt-1">{t('download.comingSoon')}</p>
            </div>
          ) : (
            <a href={href} data-testid="download-app-link">
              <AppStoreBadge className="h-10 inline-block" />
            </a>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}

function EarlyAccessBanner() {
  const { t } = useTranslation('auth');

  return (
    <div className="max-w-[400px] w-full mt-5">
      <div className="rounded-lg border border-amber-400/50 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-950/40 px-4 py-3.5">
        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {t('earlyAccess.title')}
            </p>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
              {t('earlyAccess.description')}
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-2 ms-8">
          <div className="flex items-baseline gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-200/60 dark:bg-amber-800/40 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:text-amber-200 whitespace-nowrap">
              {t('earlyAccess.beta')}
            </span>
            <span className="text-xs text-amber-800 dark:text-amber-300">
              {t('earlyAccess.betaDescription')}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-200/60 dark:bg-orange-800/40 px-2.5 py-0.5 text-xs font-semibold text-orange-900 dark:text-orange-200 whitespace-nowrap">
              {t('earlyAccess.alpha')}
            </span>
            <span className="text-xs text-orange-800 dark:text-orange-300">
              {t('earlyAccess.alphaDescription')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const MAX_RESEND_ATTEMPTS = 3;
const RESEND_COOLDOWN_MS = 60000; // 1 minute between resends

// Pre-fill credentials only for actual local development (not Capacitor mobile apps)
const DEFAULT_EMAIL = isLocalDev ? 'admin@zvi.net' : '';
const DEFAULT_PASSWORD = isLocalDev ? 'admin123' : '';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Preserve the query string AND hash of the originally-requested page (not
  // just its pathname) so deep links with params — /settings?tab=notifications,
  // /correspondence/go?type=ticket&id=… — survive the login round-trip.
  const returnTo = getReturnPathFromState(location.state);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const { login, completeMfaLogin } = useAuth();

  // Default the UI language from the visitor's country (e.g. Hebrew in Israel)
  // unless they've explicitly chosen one or their browser already provided a
  // supported non-English language.
  useGeoLanguageDefault();

  // Email from URL params takes precedence over defaults
  const emailFromParams = searchParams.get('email');
  const [email, setEmail] = useState(emailFromParams || DEFAULT_EMAIL);
  const [password, setPassword] = useState(DEFAULT_PASSWORD);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoadingProvider, setSSOLoadingProvider] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [lastResendTime, setLastResendTime] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeResponse | null>(null);

  const handleSSO = async (provider: SSOProviderId, providerName: string) => {
    setError(null);
    setSSOLoadingProvider(provider);

    try {
      // Store return URL for post-SSO redirect
      if (returnTo !== DEFAULT_RETURN_PATH) {
        setSSOItem('sso_return_to', returnTo);
      }

      // Native iOS Sign in with Apple — hand off to the system sheet rather
      // than the OAuth web redirect. The callback response shape is identical
      // to the web flow, so we hand it off to the standard SSOCallbackPage
      // via sessionStorage and a `native=1` URL flag.
      if (provider === 'apple' && supportsAppleNativeSignIn()) {
        const bundleId = (import.meta.env.VITE_IOS_BUNDLE_ID as string | undefined) || 'ai.panluma.app';
        const result = await triggerAppleNativeSignIn(bundleId);
        const callbackResp = await authApi.ssoNativeCallback(
          'apple',
          result.identityToken,
          bundleId,
          result.user || null,
        );
        sessionStorage.setItem('__panluma_native_sso_response', JSON.stringify(callbackResp));
        navigate('/auth/sso/callback?native=1');
        return;
      }

      // Build the redirect URI for the SSO callback. Apple requires a backend
      // POST endpoint (response_mode=form_post); other providers go directly
      // to the SPA callback page.
      const redirectUri = provider === 'apple' ? getAppleOAuthRedirectUri() : getOAuthRedirectUri();

      // Check if we're on a custom domain - need cross-domain SSO flow
      if (isCustomDomain()) {
        // Target domain to hand the session back to after auth completes.
        const targetDomain = getCurrentDomain();

        // Mint the OAuth state on the MAIN domain so the backend's per-flow
        // httpOnly binding cookie lands there (the only origin the cross-domain
        // callback can read it) — closes the login-CSRF / session-fixation gap.
        // sessionStorage set here lives on the custom domain and is unreadable at
        // the main-domain callback, so we don't store state client-side; the
        // callback validates the server-issued, browser-bound nonce instead.
        window.location.href = authApi.buildCrossDomainInitUrl(provider, redirectUri, targetDomain);
        return;
      } else {
        // Standard flow - same domain
        // For native apps, encode platform in state so the trampoline page
        // knows which custom scheme to redirect to (panluma:// or ai.panluma.app://)
        const nativePlatform = getNativePlatform();
        const stateExtra = nativePlatform ? { platform: nativePlatform } : undefined;
        const { auth_url, state } = await authApi.ssoInit(provider, redirectUri, stateExtra);

        // Store state for verification on callback
        setSSOItem('sso_state', state);
        setSSOItem('sso_redirect_uri', redirectUri);
        setSSOItem('sso_provider', provider);

        const result = await launchSSO(auth_url);
        if (result.status === 'cancelled') {
          setSSOLoadingProvider(null);
          return;
        }
        if (result.status === 'completed') {
          navigate(result.inAppPath);
          return;
        }
      }
    } catch (err) {
      console.error(`[SSO] ${providerName} SSO failed:`, err);
      if (err instanceof AuthApiError) {
        setError(t('sso.providerError', { provider: providerName }));
      } else {
        setError(t('sso.error'));
      }
      setSSOLoadingProvider(null);
    }
  };

  const handleResendVerification = async () => {
    const now = Date.now();
    if (resendCount >= MAX_RESEND_ATTEMPTS) {
      setResendMessage(t('verification.maxAttemptsReached', 'Maximum resend attempts reached. Please try again later.'));
      return;
    }
    if (now - lastResendTime < RESEND_COOLDOWN_MS) {
      const secondsLeft = Math.ceil((RESEND_COOLDOWN_MS - (now - lastResendTime)) / 1000);
      setResendMessage(t('verification.waitBeforeResend', { seconds: secondsLeft, defaultValue: `Please wait ${secondsLeft} seconds before requesting another email.` }));
      return;
    }

    setIsResending(true);
    setResendMessage(null);

    try {
      await authApi.resendVerification({ email: email.trim().toLowerCase() });
      setResendCount((c) => c + 1);
      setLastResendTime(now);
      // Clear the error and show success message
      setError(null);
      setErrorCode(null);
      setResendMessage(t('verification.resendSuccess'));
    } catch {
      setResendMessage(t('verification.resendFailed'));
    } finally {
      setIsResending(false);
    }
  };

  // Finish a completed login: redirect to the user's custom domain (if any)
  // or navigate to the originally requested page. Shared by the password flow
  // and the MFA challenge flow.
  const finishLogin = async (redirectUrl: string | null) => {
    // If user has a custom domain, redirect them there with cross-domain auth.
    // Skip cross-domain redirect on Capacitor mobile apps — they connect directly to API.
    if (redirectUrl && !isCapacitor) {
      const adaptedRedirectUrl = adaptCrossDomainUrl(redirectUrl);
      const storedAccessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      const storedRefreshToken = await getRefreshToken();
      const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);

      if (storedAccessToken && storedRefreshToken && storedUser) {
        try {
          const targetDomain = new URL(adaptedRedirectUrl).hostname;
          const { code } = await authApi.createCrossDomainCode(
            targetDomain,
            storedAccessToken,
            storedRefreshToken,
            JSON.parse(storedUser)
          );
          // Forward the requested deep link so a custom-domain login lands the
          // user on the page they originally clicked (e.g. a /correspondence/go
          // bounce), not just their home root. token-exchange re-validates `next`
          // as same-origin before redirecting.
          const nextParam =
            returnTo !== DEFAULT_RETURN_PATH ? `&next=${encodeURIComponent(returnTo)}` : '';
          window.location.href = `${adaptedRedirectUrl}/auth/token-exchange?code=${encodeURIComponent(code)}${nextParam}`;
          return;
        } catch {
          window.location.href = adaptedRedirectUrl;
          return;
        }
      }
    }

    // No custom domain redirect - navigate to original URL or home.
    // With no explicit return target, honour the user's locked starting
    // module (T11) when one is configured.
    if (returnTo === DEFAULT_RETURN_PATH) {
      navigate(resolveStartingModulePath() ?? returnTo, { replace: true });
      return;
    }
    navigate(returnTo, { replace: true });
  };

  // Resolve the path of the signed-in user's locked starting module, if set.
  // Reads the freshly-stored user blob — auth state may not have flushed yet.
  const resolveStartingModulePath = (): string | null => {
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);
    if (!storedUser) return null;
    try {
      const parsed = JSON.parse(storedUser) as { starting_module?: string | null };
      if (!parsed.starting_module) return null;
      return getModuleById(parsed.starting_module)?.defaultPath ?? null;
    } catch {
      return null;
    }
  };

  // Called by MfaChallengeStep once the second factor is satisfied.
  const handleMfaAuthenticated = async (response: LoginResponse) => {
    const redirectUrl = await completeMfaLogin(response);
    await finishLogin(redirectUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorCode(null);
    setFieldErrors({});
    setResendMessage(null);

    // Basic validation
    const errors: Record<string, string> = {};
    if (!email.trim()) {
      errors.email = t('validation.emailRequired');
    }
    if (!password) {
      errors.password = t('validation.passwordRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const outcome = await login({ email: email.trim().toLowerCase(), password });

      // MFA required — show the challenge step instead of completing login.
      if (outcome.kind === 'mfa') {
        setMfaChallenge(outcome.challenge);
        setIsLoading(false);
        return;
      }

      await finishLogin(outcome.redirectUrl);
    } catch (err) {
      if (err instanceof AuthApiError) {
        // Check if details contain actual field validation errors (email, password)
        const fieldNames = ['email', 'password'];
        const details = err.details || {};
        const hasFieldErrors = fieldNames.some(field => field in details);

        if (err.code === 'LOGIN_ON_CUSTOM_DOMAIN' && details.url) {
          // Send the user to their tenant's domain. On native this re-points the
          // app's API at that domain in-app (no cross-origin WebView escape).
          redirectToCustomDomain(details.url as string);
          return;
        }

        if (hasFieldErrors) {
          // Field-level errors - show inline only
          setFieldErrors(details);
        } else if (err.code === 'ACCOUNT_PENDING_APPROVAL') {
          // Self-serve tenant awaiting sysadmin sign-off — show a clear,
          // translated "under review" message rather than the raw backend text.
          setError(tErrors('ACCOUNT_PENDING_APPROVAL'));
        } else if (err.code === 'ACCOUNT_APPLICATION_DECLINED') {
          // Declined applicant — show the translated decline message; the alert
          // surfaces a self-serve re-apply link (see errorCode block below).
          setError(tErrors('ACCOUNT_APPLICATION_DECLINED'));
        } else {
          // No field errors - show banner (e.g., invalid credentials, account locked)
          setError(err.message);
        }
        setErrorCode(err.code);
      } else {
        // Not an envelope from the server — a transport failure. This names the
        // two we can name (the deadline in fetchDeadline.ts → errors:TIMEOUT, a
        // dead connection → errors:NETWORK_ERROR) and falls back to the standard
        // unexpected-error copy with a reference. NOT bare `getErrorMessage`,
        // whose last resort quotes a foreign exception's own message.
        setError(getTransportErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isSSOLoading = ssoLoadingProvider !== null;
  const isAnyLoading = isLoading || isSSOLoading;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="max-w-[400px] w-full">
        <CardContent className="p-8">
          <div className="flex justify-end mb-2">
            <LanguageSelector variant="labeled" />
          </div>
          <div className="flex justify-center mb-2">
            <img src="/panluma-logo.png" alt={tCommon('app.name')} className="h-24" />
          </div>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {t('login.title')}
          </p>

          {mfaChallenge ? (
            <MfaChallengeStep
              challenge={mfaChallenge}
              email={email.trim().toLowerCase()}
              onAuthenticated={handleMfaAuthenticated}
              onBack={() => setMfaChallenge(null)}
            />
          ) : (
          <>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {error}
                {errorCode === 'EMAIL_NOT_VERIFIED' && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResending}
                      className="text-sm underline cursor-pointer hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResending ? t('verification.sending', 'Sending...') : t('verification.resendLink', 'Resend verification email')}
                    </button>
                  </div>
                )}
                {errorCode === 'ACCOUNT_APPLICATION_DECLINED' && (
                  <div className="mt-2">
                    <Link
                      to="/register"
                      className="text-sm font-medium underline hover:no-underline"
                    >
                      {t('login.reApply')}
                    </Link>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {resendMessage && (
            <Alert className="mb-4">
              <AlertDescription>{resendMessage}</AlertDescription>
            </Alert>
          )}

          {searchParams.get('account_deleted') === 'true' && (
            <Alert className="mb-4">
              <AlertDescription>{t('login.accountDeleted')}</AlertDescription>
            </Alert>
          )}

          {/* SSO Buttons */}
          <SSOButtonList
            loadingProvider={ssoLoadingProvider as SSOProviderId | null}
            isAnyLoading={isAnyLoading}
            onSelect={handleSSO}
          />


          <div className="relative my-6">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-sm text-muted-foreground">
              {t('sso.or')}
            </span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isAnyLoading}
                  autoComplete="username"
                  className={fieldErrors.email ? 'border-destructive' : ''}
                  data-testid="email-input"
                />
                {fieldErrors.email && (
                  <p className="text-sm text-destructive">{fieldErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isAnyLoading}
                  autoComplete="current-password"
                  className={fieldErrors.password ? 'border-destructive' : ''}
                  data-testid="password-input"
                />
                {fieldErrors.password && (
                  <p className="text-sm text-destructive">{fieldErrors.password}</p>
                )}
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  state={{ email: email.trim() }}
                  className="text-sm text-primary hover:underline"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isAnyLoading}
                data-testid="login-button"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t('login.submit')
                )}
              </Button>

              <p className="text-sm text-center">
                {t('login.noAccount')}{' '}
                <Link to="/register" className="text-primary hover:underline">
                  {t('login.signUp')}
                </Link>
              </p>
            </div>
          </form>

          <div className="relative my-6">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-sm text-muted-foreground">
              {t('demo.or')}
            </span>
          </div>

          <DemoEntry
            disabled={isAnyLoading}
            autoOpen={searchParams.get('demo') === '1'}
            reveal={searchParams.get('reveal')}
          />
          </>
          )}
        </CardContent>
      </Card>
      <EarlyAccessBanner />
      <DownloadAppPrompt />
      <p className="mt-4 text-xs text-muted-foreground text-center max-w-[400px]">
        <Trans
          i18nKey="auth:legal.footer"
          components={{
            privacyLink: <a href="https://www.panluma.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
            termsLink: <a href="https://www.panluma.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" />,
          }}
        />
      </p>
    </div>
  );
}
